// Canonical session ordering/labels, matching timing-bridge/MQTT_SCHEMA.md's
// session vocabulary table. Real events only ever populate a subset of
// these - components filter to whatever's actually present in the data,
// sorted by this order, rather than assuming every code exists.
export const SESSION_ORDER = ['P1', 'P2', 'P3', 'P4', 'T1', 'T2', 'O1', 'O2', 'O3', 'O4', 'TT']

// Matches backend/live_timing_config_routes.py's VALID_SESSION_TYPES.
export const VALID_SESSION_TYPES = ['practice', 'timed', 'open', 'topten']

export const SESSION_LABELS = {
  P1: 'First Practice', P2: 'Second Practice', P3: 'Third Practice', P4: 'Fourth Practice',
  T1: 'First Timed Run', T2: 'Second Timed Run',
  O1: 'Open Session', O2: 'Second Open', O3: 'Third Open', O4: 'Fourth Open',
  TT: 'Top Ten Run-Off',
}

/**
 * Timed/record-eligible sessions only (Timed + Top Ten, per MQTT_SCHEMA.md -
 * Practice and Open are both untimed). Interim: derived from the code
 * prefix, since the wire data has no explicit per-session type yet - once
 * the Live Timing Configurator (see project_live_timing_mqtt_rewrite) adds
 * real classification, this should read that instead of guessing from the
 * code string.
 */
export function isScoredSession(code) {
  return code === 'TT' || /^T\d/.test(code)
}

/**
 * Label for a session code, preferring an admin override (the 'sessions'
 * MQTT topic, {overrides: {code: {type, label}}} - see
 * timing-bridge/publishers.py::MqttPublisher.publish() and
 * backend/live_timing_config_routes.py's session_overrides table) over the
 * hardcoded default above. `overrides` is the raw topic payload's
 * `.overrides` map (or undefined/null before it arrives) - pass
 * `useMqttTopic('sessions')?.overrides` from the calling component.
 */
export function sessionLabel(code, overrides) {
  return overrides?.[code]?.label || SESSION_LABELS[code] || code
}

/** Same override-preferring pattern as sessionLabel(), for scored-session
 * styling (the chip's 'timed' CSS class) - cosmetic only, does NOT affect
 * actual results/points, which are unaffected by session_overrides (see
 * project_live_run_reclassification memory). */
export function sessionIsScored(code, overrides) {
  const overrideType = overrides?.[code]?.type
  if (overrideType) return overrideType === 'timed' || overrideType === 'topten'
  return isScoredSession(code)
}

export function sortSessionCodes(codes) {
  return [...codes].sort((a, b) => {
    const ai = SESSION_ORDER.indexOf(a)
    const bi = SESSION_ORDER.indexOf(b)
    if (ai === -1 && bi === -1) return a.localeCompare(b)
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })
}

/**
 * Whether `time` (a run in session `code`) earns the "PB" pill. The bar is
 * the rider's historical `personalBest` (fixed at entry-list import - the
 * DB only learns about today's runs once results are uploaded after the
 * event) lowered by any faster non-flagged run they've already done
 * earlier today, so a later, slower run that's still under the old
 * historical PB no longer gets a second pill. "Earlier" is SESSION_ORDER
 * order. With no `code` (a live hero card whose session isn't known yet),
 * every recorded session counts as earlier. No historical PB at all (first
 * visit to this hill) means no pill, as before.
 */
export function isRunPB(sessions, code, time, personalBest) {
  if (time == null || personalBest == null) return false
  let bar = personalBest
  for (const [c, s] of Object.entries(sessions || {})) {
    if (!s?.time || c === code) continue
    const earlier = !code || sortSessionCodes([c, code])[0] === c
    if (earlier && s.time < bar) bar = s.time
  }
  return time <= bar + 0.005
}
